import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from '../../api/axiosConfig';
import PrintableClientReport from '../printable/PrintableClientReport'; // Correct path
import LoadingSpinner from '../../components/common/LoadingSpinner';

// Define the expected structure of the fetched report data
// (Should match the structure returned by getClientReport)
interface ClientReportData {
  summary: {
    totalClients: number;
    totalSpend: number;
    totalOutstanding: number;
    averageSpend: number;
  };
  topClientsBySpend: Array<any>; // Use specific type if needed
  topClientsByOrderCount: Array<any>; // Use specific type if needed
  clientTypeDistribution: Array<{ type: string; count: number; totalSpend: number }>;
  clients: Array<{
    id: number;
    name: string;
    director?: string | null;
    phone?: string | null;
    email?: string | null;
    type?: string | null;
    referralSource?: string | null;
    discount: number;
    totalSpend: number;
    totalPaid: number;
    outstandingBalance: number;
    orderCount: number;
    averageOrderValue: number;
    firstOrderDate?: Date | string | null;
    lastOrderDate?: Date | string | null;
    preferredStructures: Array<any>; // Use specific type if needed
    averageDiscount: number;
  }>;
  startDate: string;
  endDate: string;
}

const ClientReportPrintPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  // Add other potential filters if needed (e.g., clientIds)
  // const clientIds = searchParams.get('clientIds');

  const [reportData, setReportData] = useState<ClientReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReportData = async () => {
      if (!startDate || !endDate) {
        setError('Не указан период для отчета.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        // Use the new backend endpoint for client report print data
        const response = await axios.get<ClientReportData>(`/reports/clients/print-data`, {
          params: { startDate, endDate /*, clientIds */ } // Pass filters
        });
        // Add startDate and endDate to the fetched data for the printable component
        setReportData({ ...response.data, startDate, endDate });
        // Attempt to trigger print dialog after data is loaded
        setTimeout(() => {
          // window.print();
        }, 500); // Small delay
      } catch (err: any) {
        console.error('Error fetching client report data for printing:', err);
        setError(err.response?.data?.message || 'Не удалось загрузить данные отчета для печати.');
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [startDate, endDate /*, clientIds */]); // Depend on query parameters

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="printable-container error-message">Ошибка: {error}</div>;
  }

  if (!reportData) {
    return <div className="printable-container">Данные для печати не найдены.</div>;
  }

  // Render the printable component with the fetched data
  return <PrintableClientReport reportData={reportData} />;
};

export default ClientReportPrintPage;
